import BookIcon from "@mui/icons-material/Book";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventIcon from "@mui/icons-material/Event";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Chip, Paper, Typography, alpha, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbar,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { API } from "../../../api/API";
import { AssureService } from "../../../services/assuranceService";
import formatDateTime from "../../../utils/dateFormate";
import ChangePoliceModal from "../modals/changePoliceModal";
import DeleteDialog from "../modals/DeleteModal";
import DesactiveAssureModal from "../modals/DesactiveAssureModal";
import ProfilAssureModal from "../modals/profilAssureModal";
import RegisterAssure from "../modals/RegisterAssure";
import UpdateAssureModal from "../modals/UpdateAssureModal";

// Composant pour afficher l'état actif/inactif
const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active
    ? alpha(theme.palette.success.main, 0.15)
    : alpha(theme.palette.error.main, 0.15),
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 600,
  borderRadius: "16px",
  "& .MuiChip-icon": {
    color: "inherit",
  },
}));

// Types d'actions disponibles
const actionTypes = {
  voir: "Voir",
  mjpolice: "Changer police",
  modifier: "Modifier",
  desactive: "Activer/Désactiver",
  delete: "Supprimer",
};

// Icônes pour les actions
const getActionIcon = (actionType, theme) => {
  switch (actionType) {
    case "Voir":
      return <VisibilityOutlinedIcon sx={{ color: theme.palette.info.main }} />;
    case "Changer police":
      return <BookIcon sx={{ color: theme.palette.warning.main }} />;
    case "Modifier":
      return <EditOutlinedIcon sx={{ color: theme.palette.primary.main }} />;
    case "Activer/Désactiver":
      return (
        <TipsAndUpdatesIcon sx={{ color: theme.palette.secondary.main }} />
      );
    case "Supprimer":
      return (
        <DeleteOutlineOutlinedIcon sx={{ color: theme.palette.error.main }} />
      );
    default:
      return null;
  }
};

const DataGridAssure = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50 });
  const [filterModel, setFilterModel] = useState({
    items: [],
  });

  // Modal states
  const [showAssureModal, setShowAssureModal] = useState(false);
  const [showUpdateAssureModal, setShowUpdateAssureModal] = useState(false);
  const [showMjPoliceModal, setShowMjPoliceModal] = useState(false);
  const [showDesactiveAssureModal, setShowDesactiveAssureModal] =
    useState(false);
  const [showDeleteAssureModal, setShowDeleteAssureModal] = useState(false);

  const handleAction = useCallback((row, actionType) => {
    setSelectedRow(row);
    switch (actionType) {
      case actionTypes.voir:
        setShowAssureModal(true);
        break;
      case actionTypes.modifier:
        setShowUpdateAssureModal(true);
        break;
      case actionTypes.mjpolice:
        setShowMjPoliceModal(true);
        break;
      case actionTypes.desactive:
        setShowDesactiveAssureModal(true);
        break;
      case actionTypes.delete:
        setShowDeleteAssureModal(true);
        break;
      default:
        break;
    }
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    try {
      setIsLoading(true);
      const res = await API.delete(`/assures/${selectedRow.id}`);
      if (res.status === 200 || res.status === 204) {
        toast.success("Assuré supprimé avec succès", {
          position: "bottom-right",
        });
        handleRefresh();
        setShowDeleteAssureModal(false);
      }
    } catch (error) {
      toast.error(
        `Échec de la suppression: ${error.response?.data?.message || error.message}`,
        {
          position: "bottom-right",
        }
      );
      console.error("Failed to delete:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDesactivate = async () => {
    if (!selectedRow) return;
    try {
      setIsLoading(true);
      const res = await API.patch(`/assures/${selectedRow.id}/desactive`);
      if (res.status === 200 || res.status === 204) {
        const actionType = selectedRow.is_active ? "désactivé" : "activé";
        toast.success(`Assuré ${actionType} avec succès`, {
          position: "bottom-right",
        });
        handleRefresh();
        setShowDesactiveAssureModal(false);
      }
    } catch (error) {
      toast.error(
        `Échec du changement d'état: ${error.response?.data?.message || error.message}`,
        {
          position: "bottom-right",
        }
      );
      console.error("Failed to change status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "order",
        headerName: "#",
        width: 70,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "fullName",
        headerName: "Nom Complet",
        width: 180,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: params.row.is_active
                  ? theme.palette.primary.main
                  : theme.palette.grey[500],
                fontSize: "0.8rem",
              }}
            ></Avatar>*/}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {params.value}
              </Typography>
            </Box>
          </Box>
        ),
        valueGetter: (value, r) => `${value} `,
      },
      {
        field: "date_naissance",
        headerName: "Date de Naissance",
        width: 150,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {formatDateTime(params.value)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        width: 200,
      },
      {
        field: "tel",
        headerName: "Téléphone",
        width: 140,
      },
      {
        field: "adresse",
        headerName: "Adresse",
        width: 180,
      },
      {
        field: "document",
        headerName: "Document ID",
        width: 200,
        valueGetter: (value, row) =>
          `${row.type_document_id} ${row.numero_document_id || ""}`,
      },
      {
        field: "matricule_resp",
        headerName: "Responsable",
        width: 150,
      },
      {
        field: "rfid",
        headerName: "RFID",
        width: 130,
      },
      {
        field: "is_active",
        headerName: "Statut",
        width: 120,
        renderCell: (params) => (
          <StatusChip
            icon={
              params.value ? (
                <CheckCircleOutlineIcon fontSize="small" />
              ) : (
                <CancelOutlinedIcon fontSize="small" />
              )
            }
            label={params.value ? "Actif" : "Inactif"}
            active={params.value}
            size="small"
          />
        ),
      },
      {
        field: "createdAt",
        headerName: "Créé le",
        width: 140,
        valueGetter: (value, r) => formatDateTime(value),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 100,
        disableExport: true,
        getActions: (params) => {
          const isActive = params.row.is_active;

          return Object.entries(actionTypes).map(([key, value]) => {
            let disabled = false;
            let tooltipText = value;

            // Désactiver l'option Activer/Désactiver si l'état actuel correspond déjà à l'action
            if (value === actionTypes.desactive) {
              tooltipText = isActive ? "Désactiver" : "Activer";
            }

            // Définir le texte du tooltip personnalisé pour les actions désactivées
            if (value === actionTypes.desactive) {
              if (isActive) {
                tooltipText = "Désactiver cet assuré";
              } else {
                tooltipText = "Activer cet assuré";
              }
            }

            return (
              <GridActionsCellItem
                icon={getActionIcon(value, theme)}
                label={tooltipText}
                key={key}
                onClick={() => handleAction(params.row, value)}
                showInMenu
                disabled={disabled}
                slotProps={{
                  tooltip: {
                    title: tooltipText,
                  },
                }}
              />
            );
          });
        },
      },
    ],
    [handleAction, theme]
  );

  const getSAssure = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await AssureService.getAssures();
      if (response.status === 200) {
        const { data } = response.data;
        const _data = data.map((row, index) => ({
          ...row,
          order: index + 1,
        }));
        setRows(_data);
      }
    } catch (error) {
      setError(error.message);
      toast.error(`Erreur de chargement: ${error.message}`, {
        position: "bottom-right",
      });
      console.error("Failed to fetch assures:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSAssure();
  }, []);

  const handleRefresh = () => {
    getSAssure();
  };

  const CustomToolbar = () => (
    <GridToolbarContainer
      sx={{
        backgroundColor: alpha(theme.palette.success.main, 0.05),
        p: 1,
        borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <GridToolbar
          sx={{
            "& .MuiButton-root": {
              color: theme.palette.success.main,
            },
            "& .MuiSvgIcon-root": {
              color: theme.palette.success.main,
            },
          }}
        />
      </Box>
      <RegisterAssure onRefresh={handleRefresh} />
    </GridToolbarContainer>
  );

  return (
    <Paper
      elevation={2}
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: "0 4px 20px 0 rgba(7, 65, 36, 0.1)",
      }}
    >
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Typography variant="h6" color="success" sx={{ fontWeight: 600 }}>
          Gestion des Assurés
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Liste complète des assurés enregistrés dans le système
        </Typography>
      </Box>

      <DataGrid
        slots={{
          toolbar: CustomToolbar,
        }}
        columns={columns}
        rows={rows}
        checkboxSelection
        disableRowSelectionOnClick
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: pagination.limit,
            },
          },
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={isLoading}
        autoHeight
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.dark,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
        }}
      />

      {/* Modales */}
      {showAssureModal && selectedRow && (
        <ProfilAssureModal
          show={showAssureModal}
          handleClose={() => setShowAssureModal(false)}
          assure={selectedRow}
        />
      )}

      {showUpdateAssureModal && selectedRow && (
        <UpdateAssureModal
          show={showUpdateAssureModal}
          handleClose={() => setShowUpdateAssureModal(false)}
          assure={selectedRow}
          onSuccess={handleRefresh}
        />
      )}

      {showMjPoliceModal && selectedRow && (
        <ChangePoliceModal
          show={showMjPoliceModal}
          handleClose={() => setShowMjPoliceModal(false)}
          assure={selectedRow}
          onSuccess={handleRefresh}
        />
      )}

      {showDesactiveAssureModal && selectedRow && (
        <DesactiveAssureModal
          show={showDesactiveAssureModal}
          handleClose={() => setShowDesactiveAssureModal(false)}
          handleConfirm={handleConfirmDesactivate}
          assure={selectedRow}
          message={`Voulez-vous ${selectedRow.is_active ? "désactiver" : "activer"} cet assuré ?`}
        />
      )}

      {showDeleteAssureModal && selectedRow && (
        <DeleteDialog
          isDeleteDialogOpen={showDeleteAssureModal}
          setIsDeleteDialogOpen={() => setShowDeleteAssureModal(false)}
          handleDeleteConfirm={handleDeleteConfirm}
          message={`Êtes-vous sûr de vouloir supprimer l'assuré ${selectedRow.nom} ${selectedRow.prenom} ?`}
        />
      )}
    </Paper>
  );
};

export default DataGridAssure;
